import type { OperatorMe } from "@/lib/api";

/**
 * The flows an operator may start a case on, and what to call them.
 *
 * ## The key is an address, not a name
 *
 * `workflow_key` is frozen — part of the definition table's unique constraint
 * and persisted onto every session that runs it — so it can never be edited to
 * read better. `display_name` is the presentation half and moves freely. That
 * split is what lets a flow be renamed without migrating live sessions, and it
 * is the reason this module exists rather than the dialog rendering the key.
 */

export interface WorkflowChoice {
  workflow_key: string;
  display_name: string | null;
}

/**
 * What to show for a flow.
 *
 * `display_name` wins whenever the server has one. It usually does not: the
 * column was added deliberately un-backfilled, because what these flows should
 * be *called* is an open product question, and the server passes null through
 * rather than inventing an answer.
 *
 * ⚠️ **The fallback humanises the key; it does not name the flow.** Turning
 * `high_volume` into "High Volume" is a mechanical transform of an address so
 * it is legible in a dropdown — it asserts nothing about what the flow should
 * be called. Setting `display_name` on the definition row replaces it, with no
 * code change here. Do not "improve" this into a lookup table of nicer names:
 * that would put tier-2 presentation back into front-end code, which is exactly
 * the split `verticalCopy.ts` and the `display_name` column exist to keep.
 */
export function workflowLabel(choice: WorkflowChoice): string {
  const named = choice.display_name?.trim();
  if (named) return named;
  return choice.workflow_key
    .split(/[_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * The flows to offer, or an empty list when there is nothing to choose.
 *
 * Empty is the NORMAL case, not an edge case: an org with no workflow
 * definitions — every leasing org today — has exactly one journey, and a
 * dropdown with a single "Standard" entry is a control that cannot be used.
 * Callers render the picker only when this is non-empty.
 *
 * ⚠️ Absent (`undefined`) and empty are treated alike on purpose. Absent means
 * the server predates v0.1.7; the right behaviour there is the behaviour before
 * the picker existed, which is no picker.
 *
 * `'standard'` is filtered defensively. The server already excludes it and
 * `POST /sessions` rejects it, so this should never fire — but it is the one
 * value that would render a control meaning "no flow" beside the option that
 * already means that, and the cost of the guard is a line.
 */
export function workflowChoicesFor(me: OperatorMe | null | undefined): WorkflowChoice[] {
  return (me?.org?.workflows ?? []).filter((w) => w.workflow_key !== "standard");
}

/**
 * ⚠️ There is deliberately NO "no flow chosen" sentinel any more.
 *
 * The picker used to carry an explicit `Default` option on a `__default__`
 * value, and it read badly: the trigger showed "Default" before the operator
 * had chosen anything, and then "Default" appeared again inside the list. Two
 * renderings of the same non-answer.
 *
 * Unselected is now the empty string, which is what Radix already reserves for
 * "no selection" and what the follow-up-reason select in the same dialog
 * already uses — the trigger falls back to its placeholder. The behaviour is
 * unchanged: blank still means the default journey, and the request still
 * omits `workflow_key` entirely rather than sending a magic string the server
 * would have to understand.
 */
